import { useEffect, useState } from "react";
import { collection, getDocs, limit, query, where } from "firebase/firestore";
import { useAuth, useFirestore } from "reactfire";
import { AdminProfile, getAllowedModules } from "../utils/admin_access";

export default function useAdminAccess() {
  const auth = useAuth();
  const firestore = useFirestore();
  const [profile, setProfile] = useState<AdminProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasResolved, setHasResolved] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadAdminAccess() {
      if (!auth.currentUser?.uid) {
        if (isMounted) {
          setProfile(null);
          setIsLoading(false);
          setHasResolved(true);
        }
        return;
      }

      setIsLoading(true);
      setHasResolved(false);

      try {
        const adminQuery = query(
          collection(firestore, "admins"),
          where("uid", "==", auth.currentUser.uid),
          limit(1),
        );
        const adminSnapshot = await getDocs(adminQuery);

        if (!isMounted) {
          return;
        }

        setProfile((adminSnapshot.docs[0]?.data() || null) as AdminProfile | null);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error("Failed to load admin access", error);
        setProfile(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setHasResolved(true);
        }
      }
    }

    loadAdminAccess();

    return () => {
      isMounted = false;
    };
  }, [auth.currentUser?.uid, firestore]);

  const allowedModules = getAllowedModules(profile);

  return {
    profile,
    allowedModules,
    isLoading,
    hasResolved,
    isSuperAdmin: allowedModules.includes("all"),
  };
}
